import { useState, useRef, useCallback } from 'react'
import { toast } from 'sonner'
import { validateFilename, ensureExtension } from '@/lib/utils/filename'
import type { Draft } from '@/lib/storage/local-storage'

/**
 * Manages inline draft rename state and handlers.
 * Validates filenames and ensures proper extensions.
 */
export function useDraftRename(
  onRenameDraft: (draftId: string, newFilename: string) => void,
) {
  const [renamingDraftId, setRenamingDraftId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const renameInputRef = useRef<HTMLInputElement>(null)

  const handleStartRename = useCallback((draft: Draft) => {
    // Keep the full filename with extension for editing
    setRenameValue(draft.filename)
    setRenamingDraftId(draft.id)
    // Focus input after state update
    setTimeout(() => renameInputRef.current?.focus(), 0)
  }, [])

  const handleConfirmRename = useCallback(() => {
    if (renamingDraftId && renameValue.trim()) {
      const trimmed = renameValue.trim() || 'untitled'
      const validation = validateFilename(trimmed)

      if (!validation.valid) {
        toast.error(validation.error)
        // Keep editing mode open so user can fix the filename
        return
      }

      // Ensure filename always has a valid extension
      const finalFilename = ensureExtension(trimmed, validation.format)

      onRenameDraft(renamingDraftId, finalFilename)
      setRenamingDraftId(null)
      setRenameValue('')
    } else {
      // Empty filename - cancel rename
      setRenamingDraftId(null)
      setRenameValue('')
    }
  }, [renamingDraftId, renameValue, onRenameDraft])

  const handleCancelRename = useCallback(() => {
    setRenamingDraftId(null)
    setRenameValue('')
  }, [])

  const handleRenameKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleConfirmRename()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        handleCancelRename()
      }
    },
    [handleConfirmRename, handleCancelRename],
  )

  return {
    renamingDraftId,
    renameValue,
    setRenameValue,
    renameInputRef,
    handleStartRename,
    handleConfirmRename,
    handleCancelRename,
    handleRenameKeyDown,
  }
}
